/**
 * Kasa tutarları (ADR 0093 §2): kuruş (minor unit) tamsayı, her para biriminde iki hane; kayan
 * nokta yok. Metin girişi dizgeyle kuruşa çevrilir; yuvarlama yarım yukarı (sıfırdan uzağa).
 * Saf modül.
 */

/** Tek tutarın üst sınırı: 10.000.000,00 (kuruş); PostgreSQL `integer` içinde kalır. */
export const CASH_MAX_AMOUNT = 1_000_000_000;
/** Seçicide önce gelen para birimleri; başka ISO 4217 kodu da girilebilir. */
export const CASH_COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'TRY', 'NOK', 'SGD'] as const;

const CURRENCY = /^[A-Z]{3}$/;

export function isCurrency(value: unknown): value is string {
  return typeof value === 'string' && CURRENCY.test(value);
}

/** Tutar kuruş tamsayısı mı (0 – üst sınır). */
export function isCashAmount(value: unknown, min = 0): value is number {
  return (
    typeof value === 'number' && Number.isInteger(value) && value >= min && value <= CASH_MAX_AMOUNT
  );
}

/**
 * Metin tutarı kuruşa çevirir; geçersizse `null`. Ondalık ayırıcı `.` (İngilizce) ya da `,`
 * (Türkçe); öteki karakter binlik ayırıcıdır ve atılır. En çok iki ondalık; eksi yok.
 */
export function parseAmount(text: string, decimal: '.' | ',' = '.'): number | null {
  const group = decimal === '.' ? ',' : '.';
  const cleaned = text.trim().replace(/\s/g, '').split(group).join('');
  const normal = decimal === ',' ? cleaned.replace(',', '.') : cleaned;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(normal);
  if (!match) return null;
  const whole = BigInt(match[1] ?? '0');
  const fraction = BigInt((match[2] ?? '').padEnd(2, '0'));
  const minor = whole * 100n + fraction;
  return minor > BigInt(CASH_MAX_AMOUNT) ? null : Number(minor);
}

/** Kuruşu ondalık metne çevirir (`10000.50`); biçim ve gruplama arayüzün işidir. */
export function minorToDecimal(minor: number): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** `a / b` yarım yukarı (sıfırdan uzağa) yuvarlanmış tamsayı. */
export function roundHalfUp(a: bigint, b: bigint): number {
  if (b === 0n) throw new RangeError('sıfıra bölme');
  const negative = a < 0n !== b < 0n;
  const x = a < 0n ? -a : a;
  const y = b < 0n ? -b : b;
  const q = (2n * x + y) / (2n * y);
  return Number(negative ? -q : q);
}

const RATE_SCALE = 1_000_000n;

/** Döviz bozdurmada uygulanan kur: giriş / çıkış, 6 ondalık, yarım yukarı (ADR 0093 §2). */
export function exchangeRateOf(outMinor: number, inMinor: number): string {
  if (!isCashAmount(outMinor, 1) || !isCashAmount(inMinor, 1)) {
    throw new RangeError('kur için iki pozitif tutar gerekir');
  }
  const scaled = roundHalfUp(BigInt(inMinor) * RATE_SCALE, BigInt(outMinor));
  return `${Math.trunc(scaled / 1_000_000)}.${String(scaled % 1_000_000).padStart(6, '0')}`;
}

/** Tutarı kurla çevirir; yarım yukarı kuruşa yuvarlar. */
export function convertAt(minor: number, rate: string): number {
  const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(rate.trim());
  if (!match) throw new RangeError(`geçersiz kur: ${rate}`);
  const scaled = BigInt(match[1] ?? '0') * RATE_SCALE + BigInt((match[2] ?? '').padEnd(6, '0'));
  return roundHalfUp(BigInt(minor) * scaled, RATE_SCALE);
}
